import { TipoPago } from '@/types';
import { getPaymentStatusBadge } from '@/utils/paymentStatus';

type OverduePaymentRow = Record<string, unknown>;

interface OverduePaymentsTableProps {
  rows: OverduePaymentRow[];
  personType?: 'inquilino' | 'propietario';
}

function formatDate(value: unknown): string {
  if (typeof value !== 'string' || value.length === 0) return 'Sin fecha';

  const [year, month, day] = value.slice(0, 10).split('-');
  return year && month && day ? `${day}/${month}/${year}` : value;
}

function formatAmount(value: unknown, currency: unknown): string {
  const amount = Number(value);
  if (Number.isNaN(amount)) return '—';

  return new Intl.NumberFormat('es-UY', {
    style: 'currency',
    currency: typeof currency === 'string' && currency ? currency : 'UYU',
    maximumFractionDigits: 2,
  }).format(amount);
}

function getOverdueStatus(row: OverduePaymentRow) {
  return getPaymentStatusBadge({
    tipo: row.tipo === TipoPago.PROPIETARIO ? TipoPago.PROPIETARIO : TipoPago.INQUILINO,
    estado: String(row.estado ?? 'ATRASADO') as 'ATRASADO' | 'PAGADO' | 'PENDIENTE',
    fecha_limite: String(row.fecha_limite ?? ''),
  });
}

export function OverduePaymentsTable({ rows, personType = 'inquilino' }: OverduePaymentsTableProps) {
  const isOwnerReport = personType === 'propietario';
  const personColumnLabel = isOwnerReport ? 'Propietario' : 'Inquilino';
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-500">
            <th scope="col" className="p-2 text-left font-medium whitespace-nowrap">{personColumnLabel}</th>
            <th scope="col" className="p-2 text-left font-medium whitespace-nowrap">Inmueble</th>
            <th scope="col" className="p-2 text-left font-medium whitespace-nowrap">Contrato</th>
            <th scope="col" className="p-2 text-left font-medium whitespace-nowrap">Vencimiento</th>
            <th scope="col" className="p-2 text-right font-medium whitespace-nowrap">Monto</th>
            <th scope="col" className="p-2 text-left font-medium whitespace-nowrap">{isOwnerReport ? 'Estado' : 'Garantía'}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={String(row.id ?? `${row.numero_contrato ?? 'pago'}-${row.fecha_limite ?? index}`)} className="transition-colors hover:bg-gray-50">
              <td className="p-2 text-left">
                {String(isOwnerReport
                  ? row.propietario_nombre ?? 'Sin propietario asignado'
                  : row.inquilino_nombre ?? 'Sin inquilino asignado')}
              </td>
              <td className="p-2 text-left">
                {String(row.propiedad_direccion ?? 'Sin inmueble asignado')}
              </td>
              <td className="p-2 text-left whitespace-nowrap">
                {String(row.numero_contrato ?? '—')}
              </td>
              <td className="p-2 text-left whitespace-nowrap">
                {formatDate(row.fecha_limite)}
                {row.dias_atraso !== undefined && <span className="ml-2 text-xs text-red-600">({String(row.dias_atraso)} días)</span>}
              </td>
              <td className="p-2 text-right whitespace-nowrap tabular-nums">
                {formatAmount(row.monto, row.moneda)}
              </td>
              <td className={`p-2 text-left${isOwnerReport ? ' whitespace-nowrap' : ''}`}>
                {isOwnerReport ? (() => {
                  const badge = getOverdueStatus(row);
                  return badge ? (
                    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${badge.className}`}>
                      {badge.label}
                    </span>
                  ) : '—';
                })() : String(row.garantia || 'Sin garantía')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
